import { FloatingTree } from '@floating-ui/react'
import { hasBlockInteraction, useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { ButtonBlock } from '@lighthouse/block/src/blocks'
import type { ButtonBlockAbstract, FloatBlockAbstract, RichTextContentProtocol, VariableADTvalue } from '@lighthouse/core'
import { EVENT_TYPE, VariableType } from '@lighthouse/core'
import {
    checkActionEvent,
    fieldConvertValue,
    generateText,
    getWithScopeIdAndRecordIdAndColumnId,
    MultipleFloatBoxProvider,
    useActionRunningLoadings,
    useApplicationContext
} from '@lighthouse/shared'
import { reduce } from 'rambda'
import React, { useCallback, useMemo } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useFloatBlock } from '@/hooks/useFloatBlock'
import { usePageDataQueryVariableLabel } from '@/hooks/usePageDataQueryVariableLabel'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface ButtonBlockProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: ButtonBlockAbstract
}

const ButtonBlockController: React.FC<ButtonBlockProps> = ({ blockData, children, ...rest }) => {
    const {
        id,
        config: { name, floatPointerList, eventList }
    } = blockData
    const { handleActionListTrigger } = useActionTrigger(id)
    const { record } = useCustomViewBlockContext()
    const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
    const { record: viewRecord } = useCustomViewBlockContext()
    const { scope } = useContainerBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(id, scope, viewRecord?.id), [id, scope, viewRecord?.id])
    const clickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.CLICK), [eventList])
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const getPageDataQueryVariableLabel = usePageDataQueryVariableLabel()
    const parseVariable = useParseVariableWithSubscription(name)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value, {
                viewRecord,
                scope
            })
            if (!field) {
                if (value.type === VariableType.PAGE_DATA_QUERY) {
                    return getPageDataQueryVariableLabel(value)
                }
                return ''
            }

            return fieldConvertValue(field, { personOptions, roleOptions, departmentOptions })
        },
        [parseVariable, viewRecord, scope, personOptions, roleOptions, departmentOptions, getPageDataQueryVariableLabel]
    )
    const handleRenderTitle = useCallback(
        (v: RichTextContentProtocol) => {
            return generateText(v, {
                mode: 'pure-text',
                variable: {
                    renderLabel: v => parseVariable2String(v.attrs.value)
                }
            })
        },
        [parseVariable2String]
    )

    const handleEventClick = useCallback(
        (ev: React.MouseEvent | React.TouchEvent) => {
            if (!clickEvent) {
                return
            }
            return handleActionTriggerWithLoading({
                id: uniqueId,
                actorEvent: clickEvent,
                trigger: (id, actorEvent) => handleActionListTrigger(id, actorEvent, { viewRecord: record })
            })
        },
        [clickEvent, handleActionListTrigger, handleActionTriggerWithLoading, record, uniqueId]
    )

    const floatBlockList = useMemo(() => {
        return reduce<string, FloatBlockAbstract[]>(
            (pre, id) => {
                const block = blockData.children.find(v => v.id === id)
                return block ? [...pre, block as FloatBlockAbstract] : pre
            },
            [],
            floatPointerList ?? []
        )
    }, [blockData.children, floatPointerList])
    const provideValue = useFloatBlock(floatBlockList, scope)
    const hasInteraction = useMemo(() => hasBlockInteraction({ hasAction: !!clickEvent, floatBlockList }), [clickEvent, floatBlockList])

    return (
        <FloatingTree>
            <MultipleFloatBoxProvider {...provideValue}>
                <ButtonBlock
                    loading={loadings[uniqueId]}
                    stopActionPropagation={hasInteraction}
                    onClickEvent={handleEventClick}
                    onRenderTitle={handleRenderTitle}
                    blockData={blockData}
                    {...rest}
                />
                {children}
            </MultipleFloatBoxProvider>
        </FloatingTree>
    )
}

export default ButtonBlockController
